import type { ReactNode } from "react"
import { HStack, Text } from "@chakra-ui/react"
import { Chip, type ChipColours } from "../Chip"
import type { AlertCondition, AlertConditionGroup, Connective } from "./alertConditions"
import type { AlertRuleState } from "./AlertRuleCard"
import { formatChipLabel, getTimeWindowDisplayValue } from "./alertRuleFormat"
import { customAlertField, operatorLabel } from "./customAlertFields"

/*
 * The chips and one-line condition summaries every alert configuration card shows: on an
 * Asset's Alert Configuration tab and, read-only, under a System's asset alerts.
 */

// Hex, as elsewhere in sections: the theme doesn't emit Chakra's semantic colour tokens
// (bg.panel, fg.muted, border.subtle resolve to nothing). Values from the design.
export const MUTED = "#545454"
export const USER_DEFINED = "#a170d9"

// The alert tags: the site's chip (components/Chip) at one width, so a column of them lines
// up. The severity colours are the design's "proposed new tokens", not yet in the design system.
const TAG_COLOURS = {
  standard: { color: MUTED, border: "#D0D0D0", bg: "transparent" },
  custom: { color: USER_DEFINED, border: USER_DEFINED, bg: "transparent" },
  info: { color: "#1f7a44", border: "#50c878", bg: "rgba(80,200,120,.08)" },
  warning: { color: "#b3541e", border: "#f0986f", bg: "rgba(240,152,111,.10)" },
  critical: { color: "#d2042d", border: "#d2042d", bg: "rgba(210,4,45,.05)" },
} satisfies Record<string, ChipColours>

type TagKind = keyof typeof TAG_COLOURS

export function Tag({ kind, children }: { kind: TagKind; children: ReactNode }) {
  return (
    <Chip colours={TAG_COLOURS[kind]} width="90px">
      {children}
    </Chip>
  )
}

export function SeverityTag({ severity }: { severity: string }) {
  const level = severity?.toLowerCase()
  const kind: TagKind = level === "info" || level === "warning" || level === "critical" ? level : "standard"
  return <Tag kind={kind}>{formatChipLabel(severity)}</Tag>
}

export { StatusTag } from "../Chip"

export function OriginTag({ custom }: { custom: boolean }) {
  return <Tag kind={custom ? "custom" : "standard"}>{custom ? "Custom" : "Standard"}</Tag>
}

/** An AND/OR chip, as the editor's brackets draw it and the one-line summary uses it. */
export function ConnectiveChip({ connective }: { connective: Connective }) {
  return (
    <Text
      as="span"
      flexShrink={0}
      fontSize="11px"
      fontWeight="semibold"
      letterSpacing="0.05em"
      color={MUTED}
      bg="white"
      border="1px solid #a9a9a9"
      borderRadius="3px"
      px="5px"
      py="1px"
    >
      {connective}
    </Text>
  )
}

/** "5000 W", "20%", or "…" while a draft's threshold isn't a number yet. */
export function formatValue(value: number, unit: string): string {
  if (!Number.isFinite(value)) return "…"
  return `${value}${unit === "%" || !unit ? unit : ` ${unit}`}`
}

/** "Genset Module · Power above": a grouped condition, naming its Module when it has one. */
export function clauseText(condition: AlertCondition, assetNames: Record<string, string>): string {
  const field = customAlertField(condition.metric)?.label ?? formatChipLabel(condition.metric)
  const text = [field, operatorLabel(condition.operator)].filter(Boolean).join(" ")
  const asset = assetNames[condition.targetAssetId]
  return asset ? `${asset} · ${text}` : text
}

function clauseWithValue(condition: AlertCondition, assetNames: Record<string, string>): string {
  return `${clauseText(condition, assetNames)} ${formatValue(condition.threshold, customAlertField(condition.metric)?.unit ?? "")}`
}

function SummaryText({ children }: { children: ReactNode }) {
  return (
    <Text as="span" fontSize="md" whiteSpace="nowrap">
      {children}
    </Text>
  )
}

/**
 * A grouped rule on one line: every condition with its threshold, joined by AND/OR chips,
 * groups in brackets. Fades out at the right edge if it doesn't fit; the full sentence is
 * the hover title.
 */
export function RuleSummary({ group, assetNames = {} }: { group: AlertConditionGroup; assetNames?: Record<string, string> }) {
  const text = (c: AlertCondition) => clauseWithValue(c, assetNames)
  const plain = [
    ...group.conditions.map(text),
    ...group.groups.map((g) => `(${g.conditions.map(text).join(` ${g.connective.toLowerCase()} `)})`),
  ].join(` ${group.connective.toLowerCase()} `)

  const items: ReactNode[] = [
    ...group.conditions.map((c, i) => <SummaryText key={`c${i}`}>{text(c)}</SummaryText>),
    ...group.groups.map((g, gi) => (
      <HStack key={`g${gi}`} gap="6px" flexShrink={0}>
        <SummaryText>(</SummaryText>
        {g.conditions.map((c, i) => (
          <HStack key={i} gap="6px" flexShrink={0}>
            {i > 0 && <ConnectiveChip connective={g.connective} />}
            <SummaryText>{text(c)}</SummaryText>
          </HStack>
        ))}
        {g.conditions.length === 0 && <SummaryText>empty group</SummaryText>}
        <SummaryText>)</SummaryText>
      </HStack>
    )),
  ]

  return (
    <HStack
      gap="6px"
      minW={0}
      overflow="hidden"
      whiteSpace="nowrap"
      title={plain}
      aria-label={plain}
      css={{ maskImage: "linear-gradient(to right, black calc(100% - 40px), transparent)" }}
    >
      {items.map((item, i) => (
        <HStack key={i} gap="6px" flexShrink={0}>
          {i > 0 && <ConnectiveChip connective={group.connective} />}
          {item}
        </HStack>
      ))}
    </HStack>
  )
}

const TIME_UNITS = { h: "h", m: "min", s: "s" } as const

/** "Power below": the Reading a single-threshold rule judges and how it compares. */
export function conditionText(rule: AlertRuleState): string {
  const { metric, operator, thresholdDirection } = rule.template
  const field = customAlertField(metric)?.label ?? formatChipLabel(metric)
  const comparison = operatorLabel(operator) || thresholdDirection?.toLowerCase() || ""
  return [field, comparison].filter(Boolean).join(" ")
}

/**
 * What a saved rule fires on, in one line: a grouped Custom Alert's whole rule, or a
 * threshold with its value and time window. A user-defined value is in the design's purple,
 * with the default it replaces.
 */
export function AlertRuleSummary({ rule, assetNames }: { rule: AlertRuleState; assetNames?: Record<string, string> }) {
  const { template, assignment, isOverridden, effectiveThreshold } = rule
  if (template.conditionGroup) return <RuleSummary group={template.conditionGroup} assetNames={assetNames} />
  if (effectiveThreshold == null) {
    return <SummaryText>Event-based</SummaryText>
  }

  const unit = template.thresholdUnit ?? customAlertField(template.metric)?.unit ?? ""
  const timeWindow = assignment?.timeWindowOverrideSeconds ?? template.timeWindowSeconds
  const window = timeWindow != null ? getTimeWindowDisplayValue(timeWindow) : null
  // A rule with no field to name (e.g. rate_of_change) still reads as a comparison
  const lead = conditionText(rule) || formatChipLabel(template.thresholdDirection) || "Threshold"
  const userDefined = isOverridden && template.threshold != null

  return (
    <HStack gap={2} minW={0} flexWrap="wrap">
      <SummaryText>
        {lead}{" "}
        <Text as="b" color={userDefined ? USER_DEFINED : undefined}>
          {formatValue(effectiveThreshold, unit)}
        </Text>
        {window && ` within ${window.value} ${TIME_UNITS[window.unit]}`}
      </SummaryText>
      {userDefined && (
        <Text as="span" fontSize="sm" fontWeight="semibold" color={USER_DEFINED} whiteSpace="nowrap">
          User-defined · default {formatValue(template.threshold as number, unit)}
        </Text>
      )}
    </HStack>
  )
}
